import React, { ReactNode } from 'react';
import {
  Storage,
  Group,
  Sync,
  Security,
  Timeline,
  StarBorder,
  Business,
} from '@mui/icons-material';
import { formatFileSize } from '@/common/utils/fileUtils';
import {
  SubscriptionTier,
  SUBSCRIPTION_TIER_PRICES,
} from '@/common/models/enums';

export interface TierFeature {
  icon: ReactNode;
  label: string;
  value: string;
}

export interface TierConfig {
  name: string;
  price: number;
  features: TierFeature[];
  icon: ReactNode;
  bgColor: string;
  available: boolean;
}

const createTierFeatures = (
  storageBytes: number
): Record<string, TierFeature[]> => ({
  free: [
    {
      icon: React.createElement(Storage),
      label: 'Storage',
      value: formatFileSize(storageBytes),
    },
    { icon: React.createElement(Group), label: 'Projects', value: 'Unlimited' },
    { icon: React.createElement(Sync), label: 'Basic Sync', value: 'Included' },
  ],
  basic: [
    {
      icon: React.createElement(Storage),
      label: 'Storage',
      value: formatFileSize(storageBytes),
    },
    {
      icon: React.createElement(Group),
      label: 'Team Projects',
      value: 'Unlimited',
    },
    {
      icon: React.createElement(Sync),
      label: 'Advanced Sync',
      value: 'Included',
    },
    {
      icon: React.createElement(Security),
      label: 'Priority Support',
      value: 'Included',
    },
    {
      icon: React.createElement(Timeline),
      label: 'Analytics',
      value: 'Advanced',
    },
  ],
  pro: [
    {
      icon: React.createElement(Storage),
      label: 'Storage',
      value: formatFileSize(storageBytes),
    },
    {
      icon: React.createElement(Group),
      label: 'Team Projects',
      value: 'Unlimited',
    },
    {
      icon: React.createElement(Sync),
      label: 'Advanced Sync',
      value: 'Included',
    },
    {
      icon: React.createElement(Security),
      label: 'Priority Support',
      value: 'Included',
    },
    {
      icon: React.createElement(Timeline),
      label: 'Analytics',
      value: 'Advanced',
    },
  ],
  enterprise: [
    {
      icon: React.createElement(Storage),
      label: 'Storage',
      value: formatFileSize(storageBytes),
    },
    {
      icon: React.createElement(Group),
      label: 'Enterprise Teams',
      value: 'Unlimited',
    },
    {
      icon: React.createElement(Sync),
      label: 'Enterprise Sync',
      value: 'Included',
    },
    {
      icon: React.createElement(Timeline),
      label: 'Custom Analytics',
      value: 'Full Suite',
    },
  ],
});

export const getTierConfig = (
  storageBytes: number
): Record<string, TierConfig> => ({
  [SubscriptionTier.FREE]: {
    name: 'Free',
    price: SUBSCRIPTION_TIER_PRICES.FREE,
    features: createTierFeatures(storageBytes).free,
    icon: React.createElement(StarBorder, { fontSize: 'large' }),
    bgColor: 'grey.400',
    available: true,
  },
  [SubscriptionTier.BASIC]: {
    name: 'Basic',
    price: SUBSCRIPTION_TIER_PRICES.BASIC,
    features: createTierFeatures(storageBytes).basic,
    icon: React.createElement(Group, { fontSize: 'large' }),
    bgColor: 'info.main',
    available: false,
  },
  [SubscriptionTier.PRO]: {
    name: 'Pro',
    price: SUBSCRIPTION_TIER_PRICES.PRO,
    features: createTierFeatures(storageBytes).pro,
    icon: React.createElement(Timeline, { fontSize: 'large' }),
    bgColor: 'primary.main',
    available: false,
  },
  [SubscriptionTier.ENTERPRISE]: {
    name: 'Enterprise',
    price: SUBSCRIPTION_TIER_PRICES.ENTERPRISE,
    features: createTierFeatures(storageBytes).enterprise,
    icon: React.createElement(Business, { fontSize: 'large' }),
    bgColor: 'secondary.main',
    available: false,
  },
});

export const getPlanFeatures = (
  tier: string,
  storageBytes: number
): TierFeature[] => {
  const features = createTierFeatures(storageBytes);
  return features[tier.toLowerCase()] || features.free;
};
